import React, { useEffect, useRef, useState } from 'react';
import './MultiSearchSelect.scss';

export interface MultiSearchOption {
  /** Machine-readable value stored in the selection array. */
  value: string;
  /** Display label shown in the dropdown + chip. */
  label: string;
}

interface MultiSearchSelectProps {
  label: string;
  options: readonly MultiSearchOption[];
  /** Currently-selected values. Server-truth from Redux, not local state. */
  value: string[];
  onChange: (next: string[]) => void;
  /** Placeholder text on the closed-collapsed control. */
  placeholder?: string;
  /** Optional hint under the field. */
  hint?: string;
  disabled?: boolean;
  /** Show "Select all" / "Clear" quick actions. Default true. */
  quickActions?: boolean;
  className?: string;
}

/**
 * Searchable multi-select. Replaces raw chip-picker rows on filter
 * surfaces where the option set is large enough to benefit from
 * search + quick actions. Built specifically because the discover
 * page's filter row was going to sprawl once we add
 * receipt/reputation/etc. filters — needed a compact control with a
 * single visual footprint per filter.
 *
 * UX shape (single control):
 *   [ Filter label                                 v ]
 *   ─────────────────────────────────────────────────
 *   Selected: [chip] [chip] [x clear all] [+ select all]
 *   Opened: search input + scrollable option list with checkboxes
 *
 * Deliberately generic — takes options + value + onChange. Callers
 * translate their domain types (capabilities / chains / assets /
 * whatever) into { value, label } pairs. Keeps this component
 * feature-agnostic + reusable for future filters (receipts, states,
 * date ranges will need a different picker — this covers enum-like
 * sets only).
 *
 * Not built in:
 *   - Server-side option loading (options prop is client-static)
 *   - Async search (client-side substring filter on label)
 *   - Grouping / sections (flat list only)
 *   Add when a real caller needs it.
 */
export const MultiSearchSelect: React.FC<MultiSearchSelectProps> = ({
  label,
  options,
  value,
  onChange,
  placeholder = 'Any',
  hint,
  disabled = false,
  quickActions = true,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Click-outside to close. Ignores clicks inside the container so
  // typing in the search input or clicking checkboxes doesn't
  // collapse the dropdown.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // ESC to close — same UX contract the Modal component uses.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const toggle = (val: string) => {
    if (value.includes(val)) {
      onChange(value.filter((v) => v !== val));
    } else {
      onChange([...value, val]);
    }
  };

  const clear = () => onChange([]);
  const selectAll = () => onChange(options.map((o) => o.value));

  const filtered = query.trim() === ''
    ? options
    : options.filter((o) =>
        o.label.toLowerCase().includes(query.trim().toLowerCase()),
      );

  const selectedLabels = value
    .map((v) => options.find((o) => o.value === v)?.label ?? v)
    .filter(Boolean);

  return (
    <div
      ref={containerRef}
      className={`multi-search-select ${className}`.trim()}
    >
      <label className="multi-search-select__label">{label}</label>

      <button
        type="button"
        className={`multi-search-select__control${open ? ' is-open' : ''}`}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {value.length === 0 ? (
          <span className="multi-search-select__placeholder">{placeholder}</span>
        ) : (
          <span className="multi-search-select__summary">
            {value.length === 1
              ? selectedLabels[0]
              : `${value.length} selected`}
          </span>
        )}
        <span className="multi-search-select__chevron" aria-hidden="true">▾</span>
      </button>

      {value.length > 0 && (
        <div className="multi-search-select__selected-row">
          {selectedLabels.map((label, i) => (
            <button
              key={value[i]}
              type="button"
              className="multi-search-select__chip"
              onClick={() => toggle(value[i]!)}
              disabled={disabled}
              title="Remove"
            >
              {label} <span aria-hidden="true">×</span>
            </button>
          ))}
        </div>
      )}

      {open && (
        <div className="multi-search-select__dropdown" role="listbox">
          <input
            type="search"
            className="multi-search-select__search"
            placeholder="Search…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />

          {quickActions && (
            <div className="multi-search-select__quick">
              <button
                type="button"
                className="multi-search-select__quick-btn"
                onClick={selectAll}
                disabled={value.length === options.length}
              >
                Select all
              </button>
              <button
                type="button"
                className="multi-search-select__quick-btn"
                onClick={clear}
                disabled={value.length === 0}
              >
                Clear
              </button>
            </div>
          )}

          <ul className="multi-search-select__options">
            {filtered.length === 0 && (
              <li className="multi-search-select__empty">
                No matches for "{query}"
              </li>
            )}
            {filtered.map((opt) => {
              const checked = value.includes(opt.value);
              return (
                <li key={opt.value}>
                  <label className="multi-search-select__option">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(opt.value)}
                    />
                    <span>{opt.label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {hint && <p className="multi-search-select__hint">{hint}</p>}
    </div>
  );
};

export default MultiSearchSelect;
